import { EPillar, IChallenges } from "../types/challenges";
import api from "./index";

export interface Pagination {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

type ChallengeSummary = {
  data: IChallenges[];
  totals: Record<EPillar, number>;
  pagination: Pagination;
};

export interface GetChallengesParams {
  page?: number;
  size?: number;
  pillar?: EPillar;
  status?: string;
  type?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const getChallenges = async (params: GetChallengesParams = {}): Promise<ChallengeSummary> => {
  try {

    const query: Record<string, any> = {};
    if (params.page != null) query.page = params.page;
    if (params.size != null) query.size = params.size;
    if (params.pillar) query.pillar = params.pillar;
    if (params.status) query.status = params.status;
    if (params.type) query.type = params.type;
    if (params.search) query.search = params.search;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortDir) query.sortDir = params.sortDir;

    const response = await api.get(
      `/v1/routes/gamification/backoffice/challenges/all`,
      { params: query }
    );

    const result: {
      totals: { pillar: EPillar; total: number }[];
      challenges: any[];
      pagination: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
      };
    } = response.data.data.result;

    const mappedChallenges: IChallenges[] = result.challenges.map((c: any) => ({
      id: c.id,
      name: c.name,
      description: c.description,
      criterion: c.criterion,
      criterionValue: c.criterionValue,
      criterionSecondValue: c.criterionSecondValue,
      pillar: c.pillar,
      variableId: c.variable.id,
      points: c.points,
      uniqueExecute: c.uniqueExecute,
      active: c.active,
      validUntil: c.validUntil,
      type: c.type,
    }));

    const defaultTotals: Record<EPillar, number> = {
      [EPillar.SECURITY]: 0,
      [EPillar.ESG]: 0,
      [EPillar.PREDICTIVE]: 0,
      [EPillar.SAFE]: 0,
    };

    const totals: Record<EPillar, number> = result.totals.reduce(
      (acc, t) => {
        acc[t.pillar] = t.total;
        return acc;
      },
      { ...defaultTotals }
    );

    return {
      data: mappedChallenges,
      totals,
      pagination: result.pagination,
    };
  } catch (error) {
    console.error('Erro ao buscar desafios:', error);
    return {
      data: [],
      totals: {
        [EPillar.SECURITY]: 0,
        [EPillar.ESG]: 0,
        [EPillar.PREDICTIVE]: 0,
        [EPillar.SAFE]: 0,
      },
      pagination: {
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
      },
    };
  }
};

export const saveChallenge = async (challenge: IChallenges): Promise<void> => {
  const response = await api.post(`/v1/routes/gamification/backoffice/challenges`, {
    id: 0,
    name: challenge.name,
    description: challenge.description,
    criterion: challenge.criterion,
    criterionValue: challenge.criterionValue,
    criterionSecondValue: challenge.criterionSecondValue,
    pillar: challenge.pillar,
    variableId: challenge.variableId,
    points: challenge.points,
    uniqueExecute: challenge.uniqueExecute,
    active: challenge.active,
    validUntil: new Date(challenge.validUntil).toISOString(),
    type: challenge.type
  });
  return Promise.resolve();
};

export const updateChallenge = async (
  id: string,
  challenge: IChallenges
): Promise<void> => {
  const response = await api.post(`/v1/routes/gamification/backoffice/challenges`, {
    id: challenge.id,
    name: challenge.name,
    description: challenge.description,
    criterion: challenge.criterion,
    criterionValue: challenge.criterionValue,
    criterionSecondValue: challenge.criterionSecondValue,
    pillar: challenge.pillar,
    variableId: challenge.variableId,
    points: challenge.points,
    uniqueExecute: challenge.uniqueExecute,
    active: challenge.active,
    validUntil: new Date(challenge.validUntil).toISOString(),
    type: challenge.type
  });
  return Promise.resolve();
};

export const toggleChallengeStatus = async (id: string, status: boolean): Promise<void> => {
  try {
    const response = await api.patch(`/v1/routes/gamification/backoffice/challenges/${id}/toggle`, {
      active: status
    });
    return Promise.resolve();
  } catch (error) {
    console.error('Erro ao alterar status do desafio:', error);
    throw error;
  }
};


export const getVariablesAll = async (): Promise<any> => {
  try {
    const response = await api.get(`v1/routes/gamification/backoffice/variable/all`);
    const variables = response.data?.data?.result || [];

    return variables.map((variable: any) => ({
      value: variable.id,
      label: variable.name,
    }));
  } catch (error) {
    console.error('Erro ao buscar variáveis:', error);
    throw error; // Re-lança o erro para que o controller possa capturar
  }
};